import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { ChangePricingPlanCardModalForm } from 'src/features/cards/components/ChangePricingPlanCardModalForm.tsx';
import EditCardIdentifierModalForm from 'src/features/cards/components/EditCardIdentifierModalForm';
import { ExitInParkingCardModalForm } from 'src/features/cards/components/ExitInParkingCardModalForm.tsx';
import { MakeGuestInParkingCardModalForm } from 'src/features/cards/components/MakeGuestInParkingCardModalForm.tsx';
import { PayInParkingCardModalForm } from 'src/features/cards/components/PayInParkingCardModalForm.tsx';
import { CARD_TYPES, type InParkingCard } from 'src/features/cards/types';
import { cardMessages } from 'src/features/cards/utils/messages.ts';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { Button } from 'src/registry/ui/Button.tsx';
import { DialogTrigger } from 'src/registry/ui/Dialog.tsx';
import { generalMessages } from 'src/utils/messages';

import type { InParkingCardDetail } from 'src/features/cards/types';

type CardModalFormButtonsProps = {
    data?: InParkingCardDetail | null;
    cardInParkingData?: InParkingCard | null;
    refetch: () => void;
};

const CardModalFormButtons = ({ data, cardInParkingData, refetch }: CardModalFormButtonsProps) => {
    const { formatMessage } = useIntl();

    const permissionPayment = useHasPermissions([PERMISSION.VEHICLE_STAY_TAKE_PAYMENT]);
    const permissionExit = useHasPermissions([PERMISSION.VEHICLE_STAY_EXIT]);
    const permissionMakeGuest = useHasPermissions([PERMISSION.VEHICLE_STAY_MAKE_GUEST]);
    const permissionChangePricing = useHasPermissions([PERMISSION.VEHICLE_STAY_CHANGE_PRICING]);
    const permissionChangeIdentifier = useHasPermissions([PERMISSION.VEHICLE_STAY_CHANGE_IDENTIFIER]);

    const isMakeGuestBtnDisabled = data?.card_type !== CARD_TYPES.NORMAL || data?.is_guest === true;

    return (
        <>
            {data ? (
                <div className="grid gap-2">
                    {permissionChangeIdentifier && (
                        <EditCardIdentifierModalForm inParkingCardDetail={data} refetch={refetch}>
                            <DialogTrigger asChild>
                                <Button>{formatMessage(generalMessages.edit)}</Button>
                            </DialogTrigger>
                        </EditCardIdentifierModalForm>
                    )}
                    <div className="xs:grid-cols-2 grid gap-2">
                        {permissionPayment && (
                            <PayInParkingCardModalForm inParkingCardDetail={data} refetch={refetch}>
                                <DialogTrigger asChild>
                                    <Button disabled={data?.due_amount_with_tax === 0}>
                                        {formatMessage(cardMessages.payCard)}
                                    </Button>
                                </DialogTrigger>
                            </PayInParkingCardModalForm>
                        )}
                        {permissionExit && (
                            <ExitInParkingCardModalForm inParkingCardDetail={data} refetch={refetch}>
                                <DialogTrigger asChild>
                                    <Button disabled={data.due_amount > 0} variant="destructiveLight">
                                        {formatMessage(cardMessages.exitFromParking)}
                                    </Button>
                                </DialogTrigger>
                            </ExitInParkingCardModalForm>
                        )}
                        {permissionMakeGuest && (
                            <MakeGuestInParkingCardModalForm inParkingCardDetail={data} refetch={refetch}>
                                <DialogTrigger asChild>
                                    <Button variant="outline" disabled={isMakeGuestBtnDisabled}>
                                        {formatMessage(cardMessages.makeGuest)}
                                    </Button>
                                </DialogTrigger>
                            </MakeGuestInParkingCardModalForm>
                        )}
                        {permissionChangePricing && (
                            <ChangePricingPlanCardModalForm inParkingCardDetail={cardInParkingData} refetch={refetch}>
                                <DialogTrigger asChild>
                                    <Button variant="outline">{formatMessage(cardMessages.changePlan)}</Button>
                                </DialogTrigger>
                            </ChangePricingPlanCardModalForm>
                        )}
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-2 grid-rows-2 gap-4">
                    {permissionPayment && <Button disabled={!data}>{formatMessage(cardMessages.payCard)}</Button>}
                    {permissionExit && (
                        <Button disabled={!data} variant="destructiveLight">
                            {formatMessage(cardMessages.exitFromParking)}
                        </Button>
                    )}
                    {permissionMakeGuest && (
                        <Button variant="outline" disabled={!data}>
                            {formatMessage(cardMessages.makeGuest)}
                        </Button>
                    )}
                    {permissionChangePricing && (
                        <Button variant="outline" disabled={!data}>
                            {formatMessage(cardMessages.changePlan)}
                        </Button>
                    )}
                </div>
            )}
        </>
    );
};

export default CardModalFormButtons;
